import { ref, watch, type ComputedRef, type Ref } from 'vue'

import type { WorkflowInstance } from '@/types/workflow'

import {
  buildInsertedStoryboardScene,
  cloneScene,
  mergeStoryboardSceneServerFields,
  normalizeStoryboardSceneDraft,
} from './storyboardSceneDrafts'
import type { StoryboardSceneDraft, StoryboardWorkspaceState } from './types'
import { buildStoryboardEditorText } from './workspacePresentation'

type StoryboardWorkspaceTab = 'script' | 'consistency' | 'generation'

interface UpdateStageContentOptions {
  stage: string
  content: string
}

interface PersistOptions {
  successMessage?: string
  silent?: boolean
}

interface UseStoryboardDraftWorkspaceOptions {
  projectId: number
  workflow: Ref<WorkflowInstance | null>
  storyboardScenes: ComputedRef<StoryboardSceneDraft[]>
  storyboardWorkspaceState: ComputedRef<StoryboardWorkspaceState>
  sceneImageInputs: Ref<Record<number, string>>
  sceneVideoFeedbackDrafts: Ref<Record<number, string>>
  editorContent: Ref<string>
  editorDirty: Ref<boolean>
  selectedStoryboardSceneNumber: Ref<number | null>
  hoveredStoryboardSceneNumber: Ref<number | null>
  storyboardWorkspaceTab: Ref<StoryboardWorkspaceTab>
  reviewFeedback: Ref<string>
  getStoryboardWorkspaceStatePayload: () => StoryboardWorkspaceState
  persistStoryboardWorkspaceState: (nextState: StoryboardWorkspaceState, options?: PersistOptions) => Promise<void>
  updateStageContent: (projectId: number, options: UpdateStageContentOptions) => Promise<WorkflowInstance>
  getActionErrorMessage: (error: any) => string | null
  resolveDuration: (scene?: Record<string, any> | null) => number
  buildImagePrompt: (scene: Record<string, any>) => string
  buildVideoPrompt: (scene: Record<string, any>) => string
  confirmDeleteScene: (sceneNumber: number) => Promise<boolean>
  onRegenerateStoryboard: (payload: { feedback: string; editedContent?: string }) => Promise<void>
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

export function useStoryboardDraftWorkspace(options: UseStoryboardDraftWorkspaceOptions) {
  const storyboardSaving = ref(false)
  const storyboardDraftDirty = ref(false)
  const storyboardDraftScenes = ref<StoryboardSceneDraft[]>([])
  const storyboardEditingSceneNumber = ref<number | null>(null)
  const storyboardEditSnapshot = ref<Record<string, any> | null>(null)

  const SIGNED_OSS_QUERY_TOKENS = [
    'ossaccesskeyid=',
    'signature=',
    'expires=',
    'x-oss-signature=',
    'x-oss-credential=',
  ]

  function normalizeComparableMediaUrl(url: unknown) {
    const normalized = String(url || '').trim()
    if (!normalized) {
      return ''
    }

    try {
      const parsed = new URL(normalized)
      const query = (parsed.search || '').toLowerCase()
      if (SIGNED_OSS_QUERY_TOKENS.some((token) => query.includes(token))) {
        parsed.search = ''
        parsed.hash = ''
        return parsed.toString()
      }
      return normalized
    } catch {
      return normalized
    }
  }

  function normalizeComparableSceneValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => normalizeComparableSceneValue(item))
    }

    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((result, [key, item]) => {
        result[key] = normalizeComparableSceneValue(item)
        return result
      }, {})
    }

    if (typeof value === 'string') {
      return normalizeComparableMediaUrl(value)
    }

    return value
  }

  function serializeComparableStoryboardScene(scene: StoryboardSceneDraft) {
    const { _clientId, ...comparable } = cloneScene(scene)
    return JSON.stringify(normalizeComparableSceneValue(comparable))
  }

  function reuseSceneReferenceIfUnchanged(
    existingScene: StoryboardSceneDraft | undefined,
    nextScene: StoryboardSceneDraft,
  ) {
    if (!existingScene) {
      return nextScene
    }

    if (serializeComparableStoryboardScene(existingScene) === serializeComparableStoryboardScene(nextScene)) {
      return existingScene
    }

    return {
      ...nextScene,
      _clientId: existingScene._clientId,
    }
  }

  let storyboardSceneClientIdSeed = 0

  function nextStoryboardSceneClientId() {
    storyboardSceneClientIdSeed += 1
    return `storyboard-scene-${storyboardSceneClientIdSeed}`
  }

  function getStoryboardSceneDraftOptions() {
    return {
      nextClientId: nextStoryboardSceneClientId,
      resolveDuration: options.resolveDuration,
      buildImagePrompt: options.buildImagePrompt,
      buildVideoPrompt: options.buildVideoPrompt,
    }
  }

  function toSceneScopedNumericRecord<T>(record?: Record<string, T> | Record<number, T> | null) {
    const result: Record<number, T> = {}
    Object.entries(record || {}).forEach(([key, value]) => {
      const sceneNumber = Number(key)
      if (sceneNumber) {
        result[sceneNumber] = value as T
      }
    })
    return result
  }

  function serializeSceneScopedRecord<T>(record: Record<number, T>) {
    return Object.entries(record).reduce<Record<string, T>>((result, [key, value]) => {
      result[String(key)] = value
      return result
    }, {})
  }

  function reindexSceneScopedRecord<T>(scenes: Array<Record<string, any>>, record: Record<number, T>) {
    return scenes.reduce<Record<number, T>>((result, scene, index) => {
      const oldSceneNumber = Number(scene.scene_number)
      if (oldSceneNumber && record[oldSceneNumber] !== undefined) {
        result[index + 1] = record[oldSceneNumber]
      }
      return result
    }, {})
  }

  async function syncStoryboardWorkspaceSceneNumbers(scenes: Array<Record<string, any>>) {
    const current = options.getStoryboardWorkspaceStatePayload()
    if (!current.scene_video_feedbacks && !current.scene_uploaded_videos && !current.scene_images) {
      return
    }

    const nextFeedbacks = reindexSceneScopedRecord(scenes, toSceneScopedNumericRecord(current.scene_video_feedbacks))
    const nextVideos = reindexSceneScopedRecord(scenes, toSceneScopedNumericRecord(current.scene_uploaded_videos))
    const nextSceneImages = reindexSceneScopedRecord(scenes, toSceneScopedNumericRecord(current.scene_images))

    await options.persistStoryboardWorkspaceState({
      ...current,
      scene_images: serializeSceneScopedRecord(nextSceneImages),
      scene_video_feedbacks: serializeSceneScopedRecord(nextFeedbacks),
      scene_uploaded_videos: serializeSceneScopedRecord(nextVideos),
    }, { silent: true })
  }

  function applyStoryboardSceneUpdateFromServer(scene: Record<string, any> | null | undefined) {
    if (!scene) {
      return
    }

    const normalizedScene = normalizeStoryboardSceneDraft(scene, Number(scene.scene_number || 1) - 1, getStoryboardSceneDraftOptions())
    const existingIndex = storyboardDraftScenes.value.findIndex((item) => item.scene_number === normalizedScene.scene_number)
    if (existingIndex < 0) {
      storyboardDraftScenes.value = [...storyboardDraftScenes.value, normalizedScene]
        .sort((left, right) => left.scene_number - right.scene_number)
      return
    }

    const nextScenes = [...storyboardDraftScenes.value]
    nextScenes[existingIndex] = mergeStoryboardSceneServerFields(nextScenes[existingIndex], normalizedScene)
    storyboardDraftScenes.value = nextScenes
  }

  function syncStoryboardDraftScenes(scenes: Array<Record<string, any> & { scene_number: number }>) {
    const normalized = scenes.map((scene, index) => normalizeStoryboardSceneDraft(scene, index, getStoryboardSceneDraftOptions()))
    const injectedPrompts = normalized.some((scene, index) => (
      scene.background_prompt !== String(scenes[index]?.background_prompt || '').trim()
        || scene.video_prompt !== String(scenes[index]?.video_prompt || '').trim()
    ))

    const existingScenes = new Map(storyboardDraftScenes.value.map((scene) => [scene.scene_number, scene] as const))
    const nextScenes = normalized.map((scene) => {
      const nextScene = (!storyboardDraftDirty.value || !storyboardDraftScenes.value.length)
        ? scene
        : mergeStoryboardSceneServerFields(existingScenes.get(scene.scene_number), scene)
      return reuseSceneReferenceIfUnchanged(existingScenes.get(scene.scene_number), nextScene)
    })

    if (!storyboardDraftDirty.value || !storyboardDraftScenes.value.length) {
      storyboardDraftDirty.value = injectedPrompts
    }

    const scenesChanged = nextScenes.length !== storyboardDraftScenes.value.length
      || nextScenes.some((scene, index) => scene !== storyboardDraftScenes.value[index])

    if (scenesChanged) {
      storyboardDraftScenes.value = nextScenes
    }

    if (!storyboardDraftScenes.value.length) {
      options.selectedStoryboardSceneNumber.value = null
      return
    }

    if (!storyboardDraftScenes.value.some((scene) => scene.scene_number === options.selectedStoryboardSceneNumber.value)) {
      options.selectedStoryboardSceneNumber.value = storyboardDraftScenes.value[0].scene_number
    }
  }

  function getRenumberedStoryboardScenes(scenes: Array<Record<string, any>>) {
    return scenes.map((scene, index) => normalizeStoryboardSceneDraft(scene, index, getStoryboardSceneDraftOptions()))
  }

  function markStoryboardDraftDirty() {
    storyboardDraftDirty.value = true
  }

  function getTrimmedPrompt(value: unknown) {
    return String(value || '').trim()
  }

  function shouldRefreshAutoPrompt(
    currentPrompt: string,
    snapshot: Record<string, any> | null,
    buildPrompt: (scene: Record<string, any>) => string,
  ) {
    if (!currentPrompt) {
      return true
    }

    if (!snapshot) {
      return false
    }

    return currentPrompt === getTrimmedPrompt(buildPrompt(snapshot))
  }

  function buildStoryboardScenesPayload() {
    return storyboardDraftScenes.value.map((scene, index) => {
      const { _clientId, ...rest } = cloneScene(scene)
      return {
        ...rest,
        scene_number: index + 1,
      }
    })
  }

  async function handleSaveStoryboardScenes(successMessage = '分镜已保存') {
    if (!storyboardDraftScenes.value.length) {
      options.error('当前没有可保存的分镜镜头')
      return false
    }

    storyboardSaving.value = true
    try {
      const payload = buildStoryboardScenesPayload()
      options.workflow.value = await options.updateStageContent(options.projectId, {
        stage: 'storyboard',
        content: JSON.stringify(payload, null, 2),
      })
      storyboardDraftDirty.value = false
      storyboardEditingSceneNumber.value = null
      options.editorContent.value = buildStoryboardEditorText({ scenes: payload })
      options.editorDirty.value = false
      options.success(successMessage)
      return true
    } catch (error: any) {
      options.error(options.getActionErrorMessage(error) || '保存分镜失败')
      return false
    } finally {
      storyboardSaving.value = false
    }
  }

  async function ensureStoryboardScenesReady() {
    if (!storyboardDraftDirty.value) {
      return true
    }
    return handleSaveStoryboardScenes('分镜已保存，可继续处理素材')
  }

  function handleAddStoryboardScene() {
    const lastSceneNumber = storyboardDraftScenes.value[storyboardDraftScenes.value.length - 1]?.scene_number || null
    handleInsertStoryboardScene(lastSceneNumber, 'after')
  }

  function handleInsertStoryboardScene(referenceSceneNumber: number | null, position: 'before' | 'after') {
    const nextScenes = storyboardDraftScenes.value.map((scene) => cloneScene(scene))
    const referenceIndex = referenceSceneNumber === null
      ? nextScenes.length - 1
      : nextScenes.findIndex((scene) => scene.scene_number === referenceSceneNumber)
    const insertionIndex = referenceIndex < 0 ? nextScenes.length : referenceIndex + (position === 'after' ? 1 : 0)
    const referenceScene = referenceIndex >= 0 ? nextScenes[referenceIndex] : nextScenes[nextScenes.length - 1]

    nextScenes.splice(insertionIndex, 0, buildInsertedStoryboardScene(referenceScene, getStoryboardSceneDraftOptions()))
    storyboardDraftScenes.value = getRenumberedStoryboardScenes(nextScenes)
    options.sceneImageInputs.value = reindexSceneScopedRecord(nextScenes, options.sceneImageInputs.value)
    options.sceneVideoFeedbackDrafts.value = reindexSceneScopedRecord(nextScenes, options.sceneVideoFeedbackDrafts.value)
    storyboardDraftDirty.value = true
    options.storyboardWorkspaceTab.value = 'script'
    options.hoveredStoryboardSceneNumber.value = null
    options.selectedStoryboardSceneNumber.value = insertionIndex + 1
    storyboardEditingSceneNumber.value = null
    void syncStoryboardWorkspaceSceneNumbers(nextScenes)
    handleToggleStoryboardSceneEdit(options.selectedStoryboardSceneNumber.value)
  }

  async function handleDeleteStoryboardScene(sceneNumber: number) {
    const confirmed = await options.confirmDeleteScene(sceneNumber)
    if (!confirmed) {
      return
    }

    const removedIndex = storyboardDraftScenes.value.findIndex((scene) => scene.scene_number === sceneNumber)
    const rawScenes = storyboardDraftScenes.value.filter((scene) => scene.scene_number !== sceneNumber)
    const nextScenes = getRenumberedStoryboardScenes(rawScenes)

    storyboardDraftScenes.value = nextScenes
    options.sceneImageInputs.value = reindexSceneScopedRecord(rawScenes, options.sceneImageInputs.value)
    options.sceneVideoFeedbackDrafts.value = reindexSceneScopedRecord(rawScenes, options.sceneVideoFeedbackDrafts.value)
    storyboardDraftDirty.value = true
    storyboardEditingSceneNumber.value = null
    options.hoveredStoryboardSceneNumber.value = null
    options.selectedStoryboardSceneNumber.value = nextScenes[Math.min(Math.max(removedIndex, 0), Math.max(nextScenes.length - 1, 0))]?.scene_number || null
    await syncStoryboardWorkspaceSceneNumbers(rawScenes)
    await handleSaveStoryboardScenes('镜头已删除')
  }

  function handleToggleStoryboardSceneEdit(sceneNumber: number | null) {
    if (!sceneNumber) {
      return
    }

    if (storyboardEditingSceneNumber.value === sceneNumber) {
      handleFinishStoryboardSceneEdit()
      return
    }

    const target = storyboardDraftScenes.value.find((scene) => scene.scene_number === sceneNumber)
    if (!target) {
      return
    }

    storyboardEditingSceneNumber.value = sceneNumber
    storyboardEditSnapshot.value = cloneScene(target)
    options.selectedStoryboardSceneNumber.value = sceneNumber
  }

  function handleCancelStoryboardSceneEdit() {
    if (!storyboardEditSnapshot.value || storyboardEditingSceneNumber.value === null) {
      storyboardEditingSceneNumber.value = null
      return
    }

    storyboardDraftScenes.value = storyboardDraftScenes.value.map((scene) => (
      scene.scene_number === storyboardEditingSceneNumber.value
        ? normalizeStoryboardSceneDraft(storyboardEditSnapshot.value as Record<string, any>, scene.scene_number - 1, getStoryboardSceneDraftOptions())
        : scene
    ))
    storyboardEditSnapshot.value = null
    storyboardEditingSceneNumber.value = null
  }

  function handleFinishStoryboardSceneEdit() {
    const sceneNumber = storyboardEditingSceneNumber.value
    const snapshot = storyboardEditSnapshot.value

    if (sceneNumber !== null) {
      const sceneIndex = storyboardDraftScenes.value.findIndex((scene) => scene.scene_number === sceneNumber)
      if (sceneIndex >= 0) {
        const currentScene = storyboardDraftScenes.value[sceneIndex]
        const refreshBackgroundPrompt = shouldRefreshAutoPrompt(
          getTrimmedPrompt(currentScene.background_prompt),
          snapshot,
          options.buildImagePrompt,
        )
        const refreshVideoPrompt = shouldRefreshAutoPrompt(
          getTrimmedPrompt(currentScene.video_prompt),
          snapshot,
          options.buildVideoPrompt,
        )
        const syncedScene = normalizeStoryboardSceneDraft({
          ...currentScene,
          background_prompt: refreshBackgroundPrompt ? '' : currentScene.background_prompt,
          video_prompt: refreshVideoPrompt ? '' : currentScene.video_prompt,
        }, sceneIndex, getStoryboardSceneDraftOptions())
        const nextScenes = [...storyboardDraftScenes.value]
        nextScenes[sceneIndex] = reuseSceneReferenceIfUnchanged(currentScene, syncedScene)
        storyboardDraftScenes.value = nextScenes
      }
    }

    storyboardEditSnapshot.value = null
    storyboardEditingSceneNumber.value = null
    storyboardDraftDirty.value = true
  }

  async function handleSaveAndRegenerateStoryboard() {
    const saved = await handleSaveStoryboardScenes('分镜已保存，开始重新生成')
    if (!saved) {
      return
    }

    if (options.workflow.value?.pending_approval?.stage !== 'storyboard') {
      options.info('当前不在分镜审批节点，已仅保存分镜内容')
      return
    }

    await options.onRegenerateStoryboard({
      feedback: options.reviewFeedback.value.trim() || '已更新分镜卡片，请按最新镜头重新生成分镜结果',
      editedContent: options.editorContent.value.trim() || undefined,
    })
  }

  watch(
    options.storyboardScenes,
    (scenes) => {
      syncStoryboardDraftScenes(scenes)
    },
    { immediate: true }
  )

  return {
    applyStoryboardSceneUpdateFromServer,
    ensureStoryboardScenesReady,
    handleAddStoryboardScene,
    handleCancelStoryboardSceneEdit,
    handleDeleteStoryboardScene,
    handleFinishStoryboardSceneEdit,
    handleInsertStoryboardScene,
    handleSaveAndRegenerateStoryboard,
    handleSaveStoryboardScenes,
    handleToggleStoryboardSceneEdit,
    markStoryboardDraftDirty,
    storyboardDraftDirty,
    storyboardDraftScenes,
    storyboardEditingSceneNumber,
    storyboardSaving,
    syncStoryboardDraftScenes,
  }
}